package net.sergeych.ugdx.realtime

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.Serializable
import net.sergeych.kiloparsec.command
import net.sergeych.mp_logger.LogTag
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.shared.api.PushPayload
import net.sergeych.ugdx.shared.api.invoke
import net.sergeych.ugdx.shared.trader.CurrencyPair
import net.sergeych.ugdx.shared.trader.StatSampleRate

@Serializable
data class WatchTraderArgs(val currencyPair: CurrencyPair, val on: Boolean)

@Serializable
data class WatchTraderLastStickArgs(val currencyPair: CurrencyPair, val statSampleRate: StatSampleRate)

val cmdWatchTrader by command<WatchTraderArgs, Unit>()
val cmdWatchUser by command<Boolean, Unit>()
val cmdSubscribeToLogs by command<Boolean, Unit>()
val cmdGetLastStick by command<WatchTraderLastStickArgs, PushPayload.JumpingCandleStick>()

internal object RealtimeChannel : LogTag("RTCHN") {

    private val access = Mutex()

    private val smPairs: MutableMap<String, SubscriptionManager> = mutableMapOf()

    private val smUser: SubscriptionManager = SubscriptionManager("user") { on ->
        cmdWatchUser(on)
    }

    private val smLogs: SubscriptionManager = SubscriptionManager("logs") {
        cmdSubscribeToLogs.invoke(it)
    }

    suspend fun subscribeToLogs(): Flow<PushPayload.SystemLog> {
        smLogs.subscribe()
        @Suppress("UNCHECKED_CAST")
        return GuestAPI().pushes.filter { it is PushPayload.SystemLog } as Flow<PushPayload.SystemLog>
    }

    suspend fun watchPair(cp: CurrencyPair, on: Boolean) {
        access.withLock {
            val sm = smPairs.getOrPut(cp.label) {
                SubscriptionManager(cp.label) {
                    cmdWatchTrader.invoke(WatchTraderArgs(cp, on))
                }
            }
            sm.subscribe(on)
        }
    }

    suspend fun watchUser(on: Boolean) {
        smUser.subscribe(true)
    }

    suspend fun getLastStick(cp: CurrencyPair, statSampleRate: StatSampleRate): PushPayload.JumpingCandleStick =
        cmdGetLastStick.invoke(WatchTraderLastStickArgs(cp, statSampleRate))

    suspend fun accountFlow(): Flow<PushPayload.AccountChanged> {
        watchUser(true)
        @Suppress("UNCHECKED_CAST")
        return GuestAPI().pushes.filter { it is PushPayload.AccountChanged } as Flow<PushPayload.AccountChanged>
    }

    @Deprecated("use GuestAPI", ReplaceWith("GuestAPI().pushes"))
    suspend fun pushes(): SharedFlow<PushPayload> = GuestAPI().pushes
}